import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowUpRight, FileText, Paperclip, Stethoscope } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import type { GeneratedDocumentOut } from '@/features/emr/api/documents-api';
import { useUploadedDocuments } from '@/features/emr/api/uploaded-documents-api';
import { DocumentViewerSheet } from '@/features/emr/components/DocumentViewerSheet';
import { UploadedDocumentSheet } from '@/features/emr/components/UploadedDocumentSheet';
import { encounterTypeBadgeStyles, encounterTypeLabels } from '@/features/emr/constants';
import { type EncounterOut, useEncounterDocuments } from '@/features/emr/api/encounters-api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface EncounterPreviewDialogProps {
  encounter: EncounterOut | null;
  onOpenChange: (open: boolean) => void;
}

export function EncounterPreviewDialog({ encounter, onOpenChange }: EncounterPreviewDialogProps) {
  const navigate = useNavigate();
  const [viewingGenerated, setViewingGenerated] = useState<GeneratedDocumentOut | null>(null);
  const [viewingUploadedId, setViewingUploadedId] = useState<string | undefined>(undefined);

  const { data: generatedDocs = [] } = useEncounterDocuments(encounter?.id);
  // Orphan (Quick Start) encounters have no patient yet, so there are no
  // chart-bound uploaded documents to fetch — skip the query entirely.
  const { data: uploadedPage } = useUploadedDocuments(
    encounter?.patient_id ? { limit: 100, patient_id: encounter.patient_id } : undefined,
  );

  const uploadedDocs = useMemo(() => {
    if (!encounter) return [];
    return (uploadedPage?.data ?? []).filter((u) =>
      (u.linked_encounters ?? []).some((l) => l.encounter_id === encounter.id),
    );
  }, [uploadedPage, encounter]);

  const chiefComplaint = (encounter?.clinical_context as { chief_complaint?: string } | null)?.chief_complaint ?? null;

  const encounterType = encounter?.encounter_type ?? 'initial';

  return (
    <>
      <Dialog open={!!encounter} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          {encounter && (
            <>
              <DialogHeader>
                <DialogTitle>{format(new Date(encounter.encounter_date), 'EEEE, MMMM d, yyyy')}</DialogTitle>
                <DialogDescription asChild>
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {encounter.encounter_type && (
                      <Badge
                        variant="outline"
                        className={cn('text-xs font-medium', encounterTypeBadgeStyles[encounterType])}>
                        {encounterTypeLabels[encounterType] ?? encounterType}
                      </Badge>
                    )}
                    {encounter.clinician && (
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Stethoscope className="h-3.5 w-3.5" />
                        {encounter.clinician.name}
                      </span>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 pt-1">
                {chiefComplaint && (
                  <section>
                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Chief Complaint
                    </h4>
                    <p className="text-sm leading-relaxed">{chiefComplaint}</p>
                  </section>
                )}

                <section>
                  <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Generated documents
                  </h4>
                  {generatedDocs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">None.</p>
                  ) : (
                    <ul className="space-y-1">
                      {generatedDocs.map((doc) => (
                        <li key={doc.id}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm hover:bg-muted/50"
                            onClick={() => setViewingGenerated(doc)}>
                            <FileText className="h-4 w-4 shrink-0 text-blue-600" />
                            <span className="flex-1 truncate">{doc.template?.name ?? 'Generated Document'}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section>
                  <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Uploaded documents
                  </h4>
                  {uploadedDocs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">None.</p>
                  ) : (
                    <ul className="space-y-1">
                      {uploadedDocs.map((doc) => (
                        <li key={doc.id}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm hover:bg-muted/50"
                            onClick={() => setViewingUploadedId(doc.id)}>
                            <Paperclip className="h-4 w-4 shrink-0 text-amber-600" />
                            <span className="flex-1 truncate">{doc.name}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>

              <Separator className="my-2" />

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false);
                    navigate({
                      to: '/encounters/$encounterId',
                      params: { encounterId: encounter.id },
                    });
                  }}>
                  Open full encounter
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DocumentViewerSheet
        open={!!viewingGenerated}
        document={viewingGenerated}
        onOpenChange={(open) => {
          if (!open) setViewingGenerated(null);
        }}
      />
      {encounter?.patient_id && (
        <UploadedDocumentSheet
          open={!!viewingUploadedId}
          documentId={viewingUploadedId}
          patientId={encounter.patient_id}
          onOpenChange={(open) => {
            if (!open) setViewingUploadedId(undefined);
          }}
        />
      )}
    </>
  );
}
